/**
 * Client-side half of the in-game bug report (`q-bug-report-button`).
 *
 * The server attaches what it owns (who, where, which run, which build). This module holds the
 * few things only the client has, and builds the bounded `bugReport` payload from them:
 *   - a short ring buffer of the client's OWN diagnostics — `console.error` / `console.warn`,
 *     uncaught errors and unhandled rejections. Never chat or the game's event logs, which carry
 *     other players' words;
 *   - the renderer the game drew with (`noteRendererKind`, set once by Game at renderer start);
 *   - the client bundle's build stamp.
 * The payload is cut to the protocol's bounds (`MAX_BUG_REPORT_*`) and, if escaping would still
 * push the envelope past the wire cap, the oldest log lines are dropped first.
 */

import {
  MAX_BUG_REPORT_CLIENT_BUILD_LENGTH,
  MAX_BUG_REPORT_LOG_LINE_LENGTH,
  MAX_BUG_REPORT_LOG_LINES,
  MAX_BUG_REPORT_NOTE_LENGTH,
  MAX_CLIENT_MESSAGE_BYTES,
} from '@great-cloud/shared/client';
import type {
  BugReportLayout,
  BugReportPayload,
  BugReportRenderer,
} from '@great-cloud/shared/client';

/** Room left for the envelope (`v`, `t`, `s`) around the payload. */
const ENVELOPE_HEADROOM_BYTES = 128;

const tail: string[] = [];
let rendererKind: BugReportRenderer | undefined;
let installed = false;

function clip(text: string, max: number): string {
  return text.length <= max ? text : `${text.slice(0, max - 1)}…`;
}

function describe(value: unknown): string {
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}

/** Add one diagnostics line to the ring (oldest dropped past `MAX_BUG_REPORT_LOG_LINES`). */
export function noteClientDiagnostic(level: 'error' | 'warn', parts: readonly unknown[]): void {
  const text = parts.map(describe).join(' ').replace(/\s+/g, ' ').trim();
  if (text.length === 0) return;
  tail.push(clip(`${level}: ${text}`, MAX_BUG_REPORT_LOG_LINE_LENGTH));
  if (tail.length > MAX_BUG_REPORT_LOG_LINES)
    tail.splice(0, tail.length - MAX_BUG_REPORT_LOG_LINES);
}

/** The ring's current lines, oldest first (a copy). */
export function clientDiagnosticsTail(): string[] {
  return [...tail];
}

/** Test seam: empty the ring and forget the renderer. */
export function resetClientDiagnostics(): void {
  tail.length = 0;
  rendererKind = undefined;
}

/** Game calls this once its renderer has started. */
export function noteRendererKind(kind: BugReportRenderer): void {
  rendererKind = kind;
}

/**
 * Start capturing: wraps `console.error` / `console.warn` (the originals still run) and listens
 * for uncaught errors and unhandled rejections. Idempotent.
 */
export function installClientDiagnostics(target: Window & typeof globalThis = window): void {
  if (installed) return;
  installed = true;
  for (const level of ['error', 'warn'] as const) {
    const original = target.console[level].bind(target.console);
    target.console[level] = (...args: unknown[]) => {
      noteClientDiagnostic(level, args);
      original(...args);
    };
  }
  target.addEventListener('error', (e: ErrorEvent) =>
    noteClientDiagnostic('error', [e.error ?? e.message]),
  );
  target.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) =>
    noteClientDiagnostic('error', ['unhandled rejection', e.reason]),
  );
}

/**
 * The `bugReport` payload for a click: the optional note (trimmed; empty ⇒ absent), the
 * diagnostics tail, renderer, layout and client build — every field inside the protocol's
 * bounds, and the whole message inside the wire cap.
 */
export function buildBugReportPayload(input: {
  note?: string;
  layout?: BugReportLayout;
  clientBuild?: string;
  log?: readonly string[];
}): BugReportPayload {
  const note = input.note?.trim() ?? '';
  const payload: BugReportPayload = {};
  if (note.length > 0) payload.note = note.slice(0, MAX_BUG_REPORT_NOTE_LENGTH);
  if (rendererKind) payload.renderer = rendererKind;
  if (input.layout) payload.layout = input.layout;
  const build = input.clientBuild?.trim() ?? '';
  if (build.length > 0) payload.clientBuild = build.slice(0, MAX_BUG_REPORT_CLIENT_BUILD_LENGTH);
  const log = (input.log ?? clientDiagnosticsTail())
    .map((l) => clip(l, MAX_BUG_REPORT_LOG_LINE_LENGTH))
    .filter((l) => l.length > 0)
    .slice(-MAX_BUG_REPORT_LOG_LINES);
  const limit = MAX_CLIENT_MESSAGE_BYTES - ENVELOPE_HEADROOM_BYTES;
  const size = (p: BugReportPayload): number => new TextEncoder().encode(JSON.stringify(p)).length;
  while (log.length > 0 && size({ ...payload, clientLog: log }) > limit) log.shift();
  if (log.length > 0) payload.clientLog = log;
  return payload;
}
